import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Property } from '../../mocks/properties';
import SaudiRiyalSymbol from '../base/icons/SaudiRiyalSymbol';
import { BedroomIcon, BathroomIcon, AreaIcon } from '../base/icons/MetricIcons';
import { formatAmount } from '../../utils/numberFormat';

interface MapPropertyCardProps {
  property: Property;
  isSelected: boolean;
  onSelect: (property: Property) => void;
  onView: (property: Property) => void;
  distanceKm?: number;
  landmarkName?: string;
}

const formatRiyalNumber = (value: number) => formatAmount(value, { maximumFractionDigits: 0 });

function MapPropertyCard({ property, isSelected, onSelect, onView, distanceKm, landmarkName }: MapPropertyCardProps) {
  const { t, i18n } = useTranslation(['search', 'property', 'common']);
  const direction = i18n.dir();
  const unitLabel = t('common:units.sqm');

  const propertyTranslations = useMemo(() => {
    const data = t('property:properties', { returnObjects: true, defaultValue: {} }) as Record<
      string,
      Record<string, unknown>
    >;
    return data ?? {};
  }, [t, i18n.language]);

  const getLocalizedField = useCallback(
    (key: string, fallback: string): string => {
      const override = propertyTranslations[property.id];
      if (override && typeof override[key] === 'string' && (override[key] as string).length > 0) {
        return override[key] as string;
      }
      return fallback;
    },
    [propertyTranslations, property.id],
  );

  const formatArea = useCallback(
    (sqft: number): string => {
      const squareMeters = Math.round(sqft / 10.7639);
      return formatAmount(squareMeters);
    },
    [],
  );

  const title = getLocalizedField('title', property.title);
  const address = getLocalizedField('address', property.address);
  const city = getLocalizedField('city', property.city);

  return (
    <div
      className={[
        'p-3 rounded-lg border cursor-pointer transition-all duration-200',
        isSelected
          ? 'border-[var(--pv-color-primary)] bg-[rgba(0,77,64,0.05)] shadow-md'
          : 'border-gray-200 dark:border-[var(--pv-border-soft)] bg-white dark:bg-[var(--pv-color-surface-strong)] hover:border-gray-300 dark:hover:border-[var(--pv-color-primary-light)]',
      ].join(' ')}
      onClick={() => onSelect(property)}
      dir={direction}
    >
      <div className="flex gap-3">
        {/* Image */}
        <div className="relative flex-shrink-0">
          <img
            src={property.imageUrl}
            alt={title}
            className="w-20 h-20 rounded-lg object-cover"
            loading="lazy"
          />
          {property.featured && (
            <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-medium rounded">
              {t('search.card.featured', { defaultValue: 'Featured' })}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-gray-900 dark:text-[var(--pv-color-text)] truncate">
            {title}
          </h4>
          <p className="text-xs text-gray-500 dark:text-[var(--pv-color-text-muted)] truncate mt-0.5">
            {address}, {city}
          </p>

          {/* Price */}
          <div className="flex items-center gap-1.5 mt-1.5">
            {!property.price || property.price <= 0 ? (
              <span className="text-sm font-bold text-[var(--pv-color-primary)]">
                {direction === 'rtl' ? 'السعر عند الطلب' : 'Price on request'}
              </span>
            ) : (
              <>
                <SaudiRiyalSymbol className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                <span className="text-sm font-bold text-[var(--pv-color-primary)]">
                  {formatRiyalNumber(property.price)}
                </span>
              </>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500 dark:text-[var(--pv-color-text-muted)]">
            <span className="flex items-center gap-1">
              <BedroomIcon className="h-3 w-3" aria-hidden />
              {property.bedrooms}
            </span>
            <span className="flex items-center gap-1">
              <BathroomIcon className="h-3 w-3" aria-hidden />
              {property.bathrooms}
            </span>
            <span className="flex items-center gap-1">
              <AreaIcon className="h-3 w-3" aria-hidden />
              {formatArea(property.sqft)} {unitLabel}
            </span>
          </div>

          {/* Distance from landmark */}
          {distanceKm !== undefined && landmarkName && (
            <div className="flex items-center gap-1 mt-1.5 text-[10px] text-[var(--pv-color-primary)]">
              <i className="ri-map-pin-line"></i>
              <span>
                {t('search.nearYou.distanceFrom', {
                  distance: distanceKm.toFixed(1),
                  landmark: landmarkName,
                  defaultValue: `${distanceKm.toFixed(1)} km from ${landmarkName}`,
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* View button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onView(property);
        }}
        className="w-full mt-2 py-1.5 text-xs font-medium text-[var(--pv-color-primary)] border border-[var(--pv-color-primary)] rounded-lg hover:bg-[var(--pv-color-primary)] hover:text-white transition-colors"
      >
        {t('search.card.viewDetails')}
      </button>
    </div>
  );
}

export default memo(MapPropertyCard);
